import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ClickCalculatorDimensionInput,
  ClickCalculatorInput,
} from '../models/click-calculation';
import {
  ClickCalculatorQuotaCell,
  ClickCalculatorQuotaResponse,
  ClickCalculatorQuotaTable,
} from '../models/click-calculator-quota';
import { IRCalculation } from '../models/ir-calculation';
import { Project } from '../models/project';
import { IncidenceRateService } from './incidence-rate.service';
import { ProjectService } from './project.service';
import { QuotaService } from './quota.service';

/** The `projectPerformanceData` fields Nav's click figure is built from. */
export interface NavClickRow {
  starts?: number | null;
  cid?: number | null;
  mobileBlock?: number | null;
  overquota?: number | null;
  navTerminate?: number | null;
  dupe?: number | null;
}

/**
 * Nav's click figure for one performance row — the shared definition used by the survey
 * performance chart and the reports builder's *Clicks*.
 *
 * Not `starts` alone: the backend's `starts` already excludes Cid, MobileBlock, Overquota, Dupe
 * and NavTerminate, so those are added back to count everyone who entered via Nav.
 */
export function navClicks(row: NavClickRow): number {
  return (
    (row.starts ?? 0) +
    (row.cid ?? 0) +
    (row.mobileBlock ?? 0) +
    (row.overquota ?? 0) +
    (row.navTerminate ?? 0) +
    (row.dupe ?? 0)
  );
}

/** The fields the input assembly consumes from a daily participants row. */
export interface ProjectPerformanceRow extends NavClickRow {
  clientTerminate: number;
  completeLois: unknown[] | null;
  vendor: string | null;
  /** Survey partner id; buy CPI is keyed on it. */
  vendorId: string | null;
}

interface ProjectParticipantsResponse {
  projectPerformanceData: ProjectPerformanceRow[];
}

/** The server-side halves of the input; the performance rows come from the caller. */
export interface ClickCalculatorSources {
  quotas: ClickCalculatorQuotaResponse | null;
  irCalculation: IRCalculation | null;
}

/** Everything a surface needs to run and present the click calculation for one project. */
export interface ClickCalculatorLoad {
  /** Ready to hand to `ClickCalculatorService.calculate()`. */
  input: ClickCalculatorInput;
  /** The raw quota payload, for surfaces that show what was excluded or scoped. */
  quotaResponse: ClickCalculatorQuotaResponse | null;
  /** True when Decipher returned quota cells, whether or not any carry a hard limit. */
  hasQuotaCells: boolean;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Builds the click calculation input from the participants feed, Decipher quota cells and the
 * saved True IR calculation. Shared so every surface feeds `calculate()` identical input.
 */
@Injectable({ providedIn: 'root' })
export class ClickCalculatorInputService {

  constructor(
    private projectService: ProjectService,
    private quotaService: QuotaService,
    private incidenceRateService: IncidenceRateService,
  ) { }

  /**
   * Errors only if the participants read fails. Quotas and True IR degrade to null, which the
   * calculation reads as "not measured".
   */
  load(project: Project): Observable<ClickCalculatorLoad> {
    return forkJoin({
      participants: this.projectService.GetProjectParticipants(project.id, '', true),
      sources: this.loadSources(project),
    }).pipe(
      map(response => this.build(
        project,
        (response.participants as ProjectParticipantsResponse).projectPerformanceData,
        response.sources,
        this.projectService.syntheticVendorId
      ))
    );
  }

  /**
   * The quota cells and saved True IR, for a surface that already holds the project's performance
   * rows and builds the input itself with `build`. `irCalculation$` shares a read the caller already
   * makes; `includePending` false skips Decipher's in-progress pull, which only tells an unreached
   * cell from one with someone mid-survey. Never errors.
   */
  loadSources(
    project: Project,
    irCalculation$?: Observable<IRCalculation | null>,
    includePending = true
  ): Observable<ClickCalculatorSources> {
    const isDecipher = project.projectSegments?.[0]?.surveyPlatForm === 'Decipher';

    return forkJoin({
      quotas: this.quotaService.GetClickCalculatorQuotas(project.id, includePending).pipe(
        catchError(() => of(null))
      ),
      // A Decipher project's hand-entered fallback counts too, whichever was saved last (NK-502).
      irCalculation: (irCalculation$ ?? this.incidenceRateService.getSavedIRCalculation(project.id, isDecipher)).pipe(
        catchError(() => of(null))
      ),
    });
  }

  /** Synthetic panel rows are left out here, as the participants call's `excludeSynthetic` does. */
  build(
    project: Project,
    performanceRows: ProjectPerformanceRow[],
    sources: ClickCalculatorSources,
    syntheticVendorId: string | null
  ): ClickCalculatorLoad {
    const rows = syntheticVendorId
      ? performanceRows.filter(row => row.vendorId !== syntheticVendorId)
      : performanceRows;
    return this.assemble(project, rows, sources.quotas, sources.irCalculation);
  }

  /**
   * Saves a whole quota's targetable flag to the project (quota-level, never per cell) and emits
   * a new input with it applied; the original input is not mutated.
   */
  saveTargeting(
    projectId: string,
    input: ClickCalculatorInput,
    dimensionId: string,
    targetable: boolean
  ): Observable<ClickCalculatorInput> {
    return this.quotaService.SetClickCalculatorTargeting(projectId, dimensionId, targetable).pipe(
      map(saved => {
        const dimensions = input.dimensions.map(dimension =>
          dimension.id === dimensionId
            ? {
              ...dimension,
              targetable: saved.targetable,
              targetingOverridden: saved.source === 'manual',
              targetingSetBy: saved.setBy,
              targetingSetAt: saved.setAt,
            }
            : dimension
        );
        // A quota moving in or out of non-targetable moves its over quotas with it.
        return {
          ...input,
          dimensions,
          nonTargetableOverQuotas: dimensions.length > 0
            ? saved.nonTargetableOverQuotaRespondents ?? this.sumNonTargetableOverQuotas(dimensions)
            : input.nonTargetableOverQuotas,
        };
      })
    );
  }

  /**
   * Non-targetable over quotas are the respondents over quota in the quotas marked non-targetable
   * (Lisa, 30 Oct 2026), the same classification the Quotas section shows, not a second selection in
   * the True IR panel. Each respondent should count once, which only the backend's
   * `nonTargetableOverQuotaRespondents` can do; this per-cell sum stands in until it ships and counts
   * a respondent over quota in two such quotas twice.
   */
  private sumNonTargetableOverQuotas(dimensions: ClickCalculatorInput['dimensions']): number {
    return dimensions
      .filter(dimension => !dimension.targetable)
      .reduce((sum, dimension) => sum + dimension.groups.reduce((groupSum, group) => groupSum + group.overQuotas, 0), 0);
  }

  private assemble(
    project: Project,
    performanceRows: ProjectPerformanceRow[],
    quotas: ClickCalculatorQuotaResponse | null,
    irCalculation: IRCalculation | null
  ): ClickCalculatorLoad {
    const rows = (performanceRows ?? []).filter(row => row.vendor != null);
    const tables = quotas?.tables ?? [];
    const qcAdjusted = this.qcRemovalsMeasured(tables);
    const dimensions = this.buildDimensions(tables, qcAdjusted);

    // Prefer the backend total; sum the rows when the endpoint returned an empty payload.
    const projectClicks = quotas?.totalsAvailable
      ? quotas.totalClicks
      : rows.reduce((sum, row) => sum + navClicks(row), 0);

    return {
      input: {
        projectGoal: project.fullLaunchQuota ?? 0,
        projectCompletes: rows.reduce((sum, row) => sum + (row.completeLois?.length ?? 0), 0),
        projectClicks,
        projectTerms: rows.reduce((sum, row) => sum + (row.clientTerminate ?? 0), 0),
        projectSpend: this.projectSpend(project, rows),
        // From the saved True IR calculation. Null (not zero) when there is none: zero would
        // claim nothing was lost to non-targetable terms.
        nonTargetableTerms: irCalculation?.ntt ?? null,
        // The non-targetable quotas' over quotas where the survey has quotas (NK-502); the saved
        // hand-entered figure only without them, as on a sample-only project.
        nonTargetableOverQuotas: dimensions.length > 0
          ? quotas?.nonTargetableOverQuotaRespondents ?? this.sumNonTargetableOverQuotas(dimensions)
          : irCalculation?.ntq ?? null,
        expectedIrPercent: project.projectSegments?.[0]?.projectIR ?? null,
        observedClicksPerDay: this.observedClicksPerDay(project, projectClicks),
        observedRateDays: project.totalDaysInField ?? 0,
        daysLeft: this.daysToFieldEnd(project),
        dimensions,
      },
      quotaResponse: quotas,
      hasQuotaCells: dimensions.some(dimension => dimension.groups.length > 0),
    };
  }

  /**
   * Sum of buy CPI × completes per vendor, skipping synthetic panel. Null if any vendor with
   * completes has no buy CPI, rather than silently treating it as free.
   */
  private projectSpend(project: Project, rows: ProjectPerformanceRow[]): number | null {
    const buyCpi = new Map<string, number>();
    for (const segment of project.projectSegments ?? []) {
      for (const partner of segment.projectSurveyPartners ?? []) {
        buyCpi.set(partner.partnerId, partner.buyCPI ?? 0);
      }
    }

    let spend = 0;
    for (const row of rows) {
      const completes = row.completeLois?.length ?? 0;
      if (completes === 0 || row.vendorId === this.projectService.syntheticVendorId) {
        continue;
      }
      const cpi = buyCpi.get(row.vendorId ?? '') ?? 0;
      if (cpi <= 0) {
        return null;
      }
      spend += cpi * completes;
    }

    return spend;
  }

  private buildDimensions(
    tables: ClickCalculatorQuotaTable[],
    qcAdjusted: boolean
  ): ClickCalculatorDimensionInput[] {
    return tables
      .filter(table => !this.isIgnoredQuota(table.table))
      .map((table, tableIndex) => {
        // Decipher labels can carry stray whitespace.
        const tableLabel = (table.table ?? '').trim();
        // Keyed on the table name, as saved targeting is.
        const dimensionId = table.table ?? `dimension-${tableIndex}`;

        return {
          id: dimensionId,
          label: tableLabel || `Quota table ${tableIndex + 1}`,
          // Classified and saved by the backend on the table's first read.
          targetable: table.targetable ?? false,
          targetingOverridden: table.targetingSource === 'manual',
          targetingSavable: (table.table ?? '').trim().length > 0,
          targetingSetBy: table.targetingSetBy ?? null,
          targetingSetAt: table.targetingSetAt ?? null,
          unattributed: table.unattributed ?? false,
          groups: (table.cells ?? [])
            .filter(cell => !this.isIgnoredQuota(cell.label))
            .map((cell, cellIndex) => ({
              id: cell.marker ?? `${tableIndex}-${cellIndex}`,
              label: (cell.label ?? '').trim(),
              // Null goal = Decipher's "unlimited" (a reporting break, not a quota). Its
              // completes still count in the IR denominators.
              hasLimit: cell.goal !== null,
              goal: cell.goal ?? 0,
              have: this.postQcCompletes(cell, qcAdjusted),
              overQuotas: cell.overquotas ?? 0,
              unreached: cell.unreached ?? false,
            })),
        };
      });
  }

  /**
   * Whether any cell carries a QC removal count. Not `attributionAvailable`, which is true on the
   * single-project path even though removals are null there.
   */
  private qcRemovalsMeasured(tables: ClickCalculatorQuotaTable[]): boolean {
    return tables.some(table =>
      (table.cells ?? []).some(cell => cell.qcRemoved !== null && cell.qcRemoved !== undefined)
    );
  }

  /**
   * Decipher's pre-QC cell completes less QC removals, matching the population Nav's total counts.
   * Floored at zero because the fallback path mixes Decipher's rollup with a null removal count.
   */
  private postQcCompletes(cell: ClickCalculatorQuotaCell, qcAdjusted: boolean): number {
    const completes = cell.completes ?? 0;
    if (!qcAdjusted) return completes;

    return Math.max(completes - (cell.qcRemoved ?? 0), 0);
  }

  /**
   * LFQ tables/cells, dropped at ingest so their completes never reach a group IR denominator.
   * Applied to both table names and cell labels.
   */
  private isIgnoredQuota(label: string | null | undefined): boolean {
    return /lfq/i.test(label ?? '');
  }

  /**
   * Signed whole days to the field end date: negative once it has passed. Not
   * `project.totalDaysLeft`, which floors at zero; falls back to it with no valid end date.
   */
  private daysToFieldEnd(project: Project): number {
    if (!project.fieldEndDate) return project.totalDaysLeft ?? 0;

    const endsAt = new Date(project.fieldEndDate);
    if (Number.isNaN(endsAt.getTime())) return project.totalDaysLeft ?? 0;

    const span = this.startOfDay(endsAt).getTime() - this.startOfDay(new Date()).getTime();
    return Math.round(span / MS_PER_DAY);
  }

  /** Local (not UTC) midnight, matching how the survey performance chart buckets days. */
  private startOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  /**
   * Clicks per calendar day since field start (paused days included), matching Field metrics'
   * day count. Zero before the first whole day, which the calculator reads as not yet rated.
   */
  private observedClicksPerDay(project: Project, projectClicks: number): number {
    const days = project.totalDaysInField ?? 0;

    return days > 0 ? projectClicks / days : 0;
  }
}
